<template>
  <teleport to="#sliding-panel-container">
    <div
      v-if="hasOverlay && isOpen"
      class="overlay position-fixed top-0 start-0 end-0 bottom-0 bg-black-50 bg-primary-black opacity-25"
      @click="handleClose"
    ></div>
    <transition :name="transitionName" appear>
      <div
        v-if="isOpen"
        class="modal-dialog-slide modal-page position-fixed top-0 bottom-0"
        :class="hasOverlay && overlayPanelClasses"
      >
        <div
          class="modal-dialog-slide modal-page position-fixed top-0 bottom-0"
          :class="panelClasses"
        >
          <div class="modal-content bg-primary-white">
            <div class="container p-2 p-md-2">
              <div class="modal-header d-flex justify-content-between py-3">
                <CloseButton
                  :classes="['btn', 'p-0', 'd-flex', 'align-items-center']"
                  @click="handleClose"
                >
                  <template #svg>
                    <ArrowLeft />
                  </template>
                  <template #text>
                    <span class="title-4 text-grey-900 text-capitalize">
                      {{ closeTitle }}
                    </span>
                  </template>
                </CloseButton>
                <CloseButton
                  :classes="['ts-x-black', 'bg-grey-200']"
                  @click="handleClose"
                ></CloseButton>
              </div>
              <div class="modal-body">
                <slot></slot>
                <div
                  class="position-fixed fixed-bottom py-2 px-4 w-100 container"
                  v-if="displayConfirmButton"
                >
                  <Button
                    class="w-100"
                    @button-click="handleConfirm"
                    :disabled="buttonDisabled"
                    :loading="isLoading"
                  >
                    <template #text>
                      {{ t('panels.confirm') }}
                    </template>
                  </Button>
                </div>
              </div>
            </div>
            <slot name="bottom">

            </slot>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts" setup>
  import { computed, defineProps, ref, watch, withDefaults } from 'vue'
  import { useStore } from '@/store'

  // UI components
  import CloseButton from '@/components/UI/Button.vue'
  import ArrowLeft from '@/assets/ArrowLeftIcon.vue'
  import Button from '@/components/UI/Button.vue'
  import { ComputedRef } from 'vue'

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t } = useI18n()

  // Props
  type SlideDirection = 'from-left' | 'from-right'

  interface Props {
    isPanelVisible: boolean
    slideDirection: SlideDirection
    onClose?: () => void
    onConfirm?: () => void
    closeTitle?: string
    displayConfirmButton?: boolean
    buttonDisabled?: boolean
    isLoading?: boolean
    hasOverlay?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    slideDirection: 'from-right',
    isPanelVisible: false,
  })

  // Refs
  const isOpen = ref(false)

  // Handle panel close
  const store = useStore()

  const handleClose = () => {
    props.onClose && props.onClose()
  }

  const handleConfirm = () => {
    props.onConfirm && props.onConfirm()
  }

  // Computed properties
  const transitionName = computed(() => {
    const name =
      props.slideDirection === 'from-left' ? 'slide-left' : 'slide-right'
    return name
  })

  const overlayPanelClasses = computed(() => {
    return {
      'w-30 position-right':
        props.hasOverlay && props.slideDirection === 'from-right',
      'w-30 position-left':
        props.hasOverlay && props.slideDirection === 'from-left',
    }
  })

  const panelClasses = computed(() => ({
    'position-right': props.slideDirection === 'from-right',
    'position-left': props.slideDirection === 'from-left',
    ...overlayPanelClasses.value,
  }))

  const openSet: ComputedRef<Set<string>> = computed(() => {
    return store.getters['modal/openSet']
  })

  // Watcher to sync panel visibility
  watch(
    () => props.isPanelVisible,
    newValue => {
      isOpen.value = newValue
      if (openSet.value.size) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'visible'
      }
    },
    { immediate: true }
  )
</script>

<style scoped>
  .modal-dialog-slide {
    width: 100vw !important;
    transition: transform 0.3s ease;
    z-index: 10000;
  }

  .overlay {
    z-index: 10000;
  }

  .modal-content {
    min-height: 100vh;
    background-color: var(--primary-white);
  }

  .modal-page {
    height: 100%;
    overflow-y: auto;
  }

  .position-right {
    right: 0;
    transform: translateX(0);
  }

  .container {
    max-width: 720px;
  }

  .position-left {
    left: 0;
    transform: translateX(0);
  }

  /* Slide transitions */
  .slide-right-enter-active,
  .slide-right-leave-active,
  .slide-left-enter-active,
  .slide-left-leave-active {
    transition: transform 0.3s ease-in-out;
  }

  .slide-right-enter-from,
  .slide-right-leave-to {
    transform: translateX(100%);
  }

  .slide-right-enter-to,
  .slide-right-leave-from {
    transform: translateX(0);
  }

  .slide-left-enter-from,
  .slide-left-leave-to {
    transform: translateX(-100%);
  }

  .slide-left-enter-to,
  .slide-left-leave-from {
    transform: translateX(0);
  }

  .h-90dvh {
    height: 90dvh;
  }

  @media (min-width: 768px) {
    .modal-dialog-slide.w-30 {
      width: 30vw !important;
    }
  }
</style>
